import { Component, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import AbstractView from '@/views/abstract-view';
import Client from '@/model/entity/client/client';
import ClientUserIndex from '@/components/client-user-index/index.vue';
import DomainIndex from '@/components/domain-index/index.vue';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import InvoiceIndex from '@/components/invoice-index/index.vue';
import ServiceIndex from '@/components/service-index/index.vue';
import TechspecIndex from '@/components/techspec-index/index.vue';

@Component({
    components: {
        'app-client-user-index': ClientUserIndex,
        'app-domain-index': DomainIndex,
        'app-invoice-index': InvoiceIndex,
        'app-service-index': ServiceIndex,
        'app-techspec-index': TechspecIndex,
    },
})
export default class ClientView extends AbstractView
{
    private client: Client|null = null;
    private tab = 'techspecs';
    private options = false;

    protected get title(): string
    {
        return this.client?.name ?? this.defaultTitle;
    }

    protected created()
    {
        this.loadClient();

        if (this.tab !== this.$route.query.tab)
        {
            this.tab = this.$route.query.tab as string;
        }
    }

    @Watch('$route')
    private onRouteChanged()
    {
        this.loadClient();

        if (this.tab !== this.$route.query.tab)
        {
            this.tab = this.$route.query.tab as string;
        }
    }

    @Watch('tab')
    private onTabChanged()
    {
        if (this.tab !== this.$route.query.tab)
        {
            const query = cloneDeep(this.$route.query);
            query.tab = this.tab;

            if (query.tab === 'techspecs')
            {
                delete query.tab;
            }

            this.$router.replace({ query }).catch();
        }
    }

    /**
     * Loads the client
     */

    private async loadClient()
    {
        try
        {
            this.client = await this.$serviceContainer.clientService.loadClient(this.$route.params.id);
        }
        catch (error: unknown)
        {
            EventBus.instance.$emit(
                'notification',
                this.$t('general.error.failed_loading_server_error'),
                'error',
            );
        }
    }

    private get googleApiKey(): string
    {
        return process.env.VUE_APP_GOOGLE_API_KEY;
    }

    private get hasComments(): boolean
    {
        return !!this.client?.comments;
    }

    private get hasContactDetails(): boolean
    {
        return this.contactDetailsArray.length > 0;
    }

    private get hasAddress(): boolean
    {
        return this.addressArray.length > 0;
    }

    private get contactDetails(): string
    {
        return this.contactDetailsArray.join('\n');
    }

    private get addressCsv(): string
    {
        return this.addressArray.join(',');
    }

    private get contactDetailsArray(): string[]
    {
        if (!this.client)
        {
            return [];
        }

        const details = [ ...this.addressArray ];

        if (this.client.url)
        {
            details.push('');
            details.push(this.client.url);
        }

        if (this.client.email)
        {
            details.push('');
            details.push(this.client.email);
        }

        return details;
    }

    private get addressArray(): string[]
    {
        const address: string[] = [];

        if (!this.client)
        {
            return address;
        }

        if (this.client.addressLine1)
        {
            address.push(this.client.addressLine1);
        }

        if (this.client.addressLine2)
        {
            address.push(this.client.addressLine2);
        }

        if (this.client.addressTown)
        {
            address.push(this.client.addressTown);
        }

        if (this.client.addressCounty)
        {
            address.push(this.client.addressCounty);
        }

        if (this.client.addressPostcode)
        {
            address.push(this.client.addressPostcode);
        }

        return address;
    }
}